import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  Signal,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faFileDashedLine, faPlus, faArrowRight, faTrash, faClone, faClose } from '@fortawesome/pro-regular-svg-icons';
import { map, tap } from 'rxjs/operators';
import { BlockModel } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { buildListQuestionRenderModel, LIST_QUESTION_BATCH_SIZE, ListQuestionRenderRow } from './list-question-render-row.model';
import { QuestionEditor } from '../../../models/question-editor.model';
import { SurveyQuestionSelectionStateService } from '../../../services/survey-editor-question-selection-state.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'empathy-list-questions',
  templateUrl: 'list-questions.component.html',
  styleUrls: ['./list-questions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListQuestionsComponent implements OnInit {
  forceActiveQuestion = input<string>();

  addPageBreak = output<void>();
  addQuestion = output<void>();
  deleteQuestions = output<void>();
  duplicateQuestions = output<void>();
  filterComments = output<string>();
  forcedActivation = output<void>();
  moveQuestions = output<void>();

  selectMode$: Signal<boolean>;
  selectedQuestions$: Signal<Set<string>>;

  activeQuestionId$ = signal<string>(null);
  count$ = signal(0);
  loading$ = signal(false);
  questions$ = signal<QuestionEditor[]>([]);
  renderRows$ = signal<ListQuestionRenderRow[]>([]);
  visibleRowCount$ = signal(LIST_QUESTION_BATCH_SIZE);

  visibleRows$ = computed(() => this.renderRows$().slice(0, this.visibleRowCount$()));

  variableQuestions$: Signal<QuestionEditor[]>;

  faArrowRight = faArrowRight;
  faClone = faClone;
  faClose = faClose;
  faFileDashedLine = faFileDashedLine;
  faPlus = faPlus;
  faTrash = faTrash;

  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private pendingScrollQuestionId: string;
  private questionScrollAnimationFrameId: number = null;
  private rowIdByQuestionId = new Map<string, string>();
  private rowIndexByQuestionId = new Map<string, number>();
  private surveyEditorService = inject(SurveyEditorService);
  private surveyQuestionSelectionStateService = inject(SurveyQuestionSelectionStateService);

  constructor() {
    this.selectMode$ = this.surveyQuestionSelectionStateService.getSelectedMode;
    this.selectedQuestions$ = this.surveyQuestionSelectionStateService.getSelectedQuestionIds;
    this.variableQuestions$ = this.surveyEditorService.surveyEditor$().sources.questions$;

    effect(
      () => {
        if (this.forceActiveQuestion()) {
          this.onActivateFromComment(this.forceActiveQuestion());
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.loading$.set(true);

    setTimeout(() => {
      this.surveyEditorService.questions$
        .pipe(
          map((questions) => questions.slice() as QuestionEditor[]),
          tap((questions: QuestionEditor[]) => this.activeQuestionId$.set(questions.find((q: QuestionEditor) => q.active === true)?.id)),
          tap((questions) => this.count$.set(questions.length)),
          tap((questions) => this.refreshRenderRows(questions)),
          tap(() => {
            this.surveyQuestionSelectionStateService.setSelectMode(false);
            this.surveyQuestionSelectionStateService.clear();
            this.loading$.set(false);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((questions) => {
          this.questions$.set(questions);
        });
    });
  }

  onActivate(questionId: string): void {
    const questionToActivate = this.surveyEditorService.surveyEditor$().questions.find((question) => question.id === questionId);

    this.activateQuestion(questionToActivate, false);
  }

  onActivateVariable(questionId: string): void {
    this.surveyEditorService.selectVariable(questionId);
  }

  onRemovePage(pageId: string): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove page',
        confirmText: 'Are you sure you want to remove this page? The questions on it will merge with the previous page.',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });
    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.removePage(pageId);
      }
    });
  }

  onSelectAll(checked: boolean): void {
    this.surveyQuestionSelectionStateService.selectAll(this.questions$(), checked);
  }

  onClearSelectedQuestions(): void {
    this.surveyQuestionSelectionStateService.clear();
  }

  onToggleSelectMode(): void {
    this.surveyQuestionSelectionStateService.setSelectMode(!this.selectMode$());
    this.surveyQuestionSelectionStateService.clear();
  }

  onLoadMore(): void {
    this.visibleRowCount$.set(Math.min(this.visibleRowCount$() + LIST_QUESTION_BATCH_SIZE, this.renderRows$().length));
  }

  private activateQuestion(question: Question | undefined, forceActivation: boolean): void {
    if (!question) {
      return;
    }

    this.activeQuestionId$.set(question.id);
    this.surveyEditorService.selectQuestion(question.id);
    this.scrollToQuestion(question.id);
    this.emitActivationSideEffects(question, forceActivation);
  }

  private onActivateFromComment(questionCode: string): void {
    const questionToActivate = this.surveyEditorService.surveyEditor$().questions.find((question) => question.code === questionCode);

    this.activateQuestion(questionToActivate, true);
  }

  private emitActivationSideEffects(question: Question, forceActivation: boolean): void {
    if (forceActivation) {
      this.surveyQuestionSelectionStateService.setSelectMode(false);
      this.surveyQuestionSelectionStateService.clear();
      this.forcedActivation.emit();
    }

    this.filterComments.emit(question.code || question.id);
  }

  private refreshRenderRows(questions: QuestionEditor[]): void {
    const blocks = this.surveyEditorService.surveyEditor$().blocks.all.value as BlockModel[];
    const renderModel = buildListQuestionRenderModel(questions, blocks);
    const previousRowCount = this.renderRows$().length;

    this.rowIdByQuestionId = renderModel.rowIdByQuestionId;
    this.rowIndexByQuestionId = renderModel.rowIndexByQuestionId;
    this.renderRows$.set(renderModel.rows);
    this.updateVisibleRowCount(renderModel.rows.length, previousRowCount);
    this.scheduleQuestionScroll();
  }

  private scrollToPendingQuestion(): void {
    if (!this.pendingScrollQuestionId) {
      return;
    }

    const rowIndex = this.rowIndexByQuestionId.get(this.pendingScrollQuestionId);
    const rowId = this.rowIdByQuestionId.get(this.pendingScrollQuestionId);

    if (rowIndex === undefined || !rowId) {
      return;
    }

    this.visibleRowCount$.set(Math.max(this.visibleRowCount$(), Math.min(rowIndex + LIST_QUESTION_BATCH_SIZE, this.renderRows$().length)));
    this.changeDetectorRef.detectChanges();
    document.getElementById(`list-questions-row-${rowId}`)?.scrollIntoView({ block: 'nearest' });
    this.pendingScrollQuestionId = null;
  }

  private scrollToQuestion(questionId: string): void {
    this.pendingScrollQuestionId = questionId;
    this.scheduleQuestionScroll();
  }

  private scheduleQuestionScroll(): void {
    if (!this.pendingScrollQuestionId) {
      return;
    }

    if (this.questionScrollAnimationFrameId !== null) {
      this.cancelAnimationFrame(this.questionScrollAnimationFrameId);
    }

    this.questionScrollAnimationFrameId = this.requestAnimationFrame(() => {
      this.questionScrollAnimationFrameId = null;
      this.scrollToPendingQuestion();
    });
  }

  private updateVisibleRowCount(rowCount: number, previousRowCount: number): void {
    if (this.visibleRowCount$() > rowCount) {
      this.visibleRowCount$.set(rowCount);
      return;
    }

    if (this.visibleRowCount$() === previousRowCount && this.visibleRowCount$() < LIST_QUESTION_BATCH_SIZE && rowCount > previousRowCount) {
      this.visibleRowCount$.set(Math.min(LIST_QUESTION_BATCH_SIZE, rowCount));
    }
  }

  private cancelAnimationFrame(animationFrameId: number): void {
    if (globalThis.cancelAnimationFrame) {
      globalThis.cancelAnimationFrame(animationFrameId);
      return;
    }

    window.clearTimeout(animationFrameId);
  }

  private requestAnimationFrame(callback: FrameRequestCallback): number {
    if (globalThis.requestAnimationFrame) {
      return globalThis.requestAnimationFrame(callback);
    }

    return window.setTimeout(() => callback(Date.now()));
  }
}
